import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { useGetLoggedInUser } from '@revelio/auth';
import {
  BuildADashTrackingEvents,
  CenterLoading,
  FeatureFlag,
} from '@revelio/core';

import { usePageVisitTracking } from '../../hooks/usePageVisitTracking';
import { useBrand } from '../../shared/branding/brand-provider';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { CustomDashPreferenceProivder } from '../../shared/user-preferences/CustomDashPreferenceProivder';
import { SyncInitialPreferencesToFilters } from '../../shared/user-preferences/SyncInitialPreferencesToFilters';
import { GlobalAIAssistantBridge } from '../global-ai-assistant/global-ai-assistant-bridge';
import { CustomDash } from './CustomDash';
import { DashboardErrorBoundary } from './components/DashboardErrorBoundary';
import { EditModeProvider } from './components/EditModeProvider/EditModeProvider';
import {
  DEFAULT_FULL_PAGE_ERROR_THRESHOLD,
  FullPageError,
} from './components/FullPageError/FullPageError';
import { PageProvider } from './components/PageConfigProvider/PageConfigProvider';
import type { Page } from './components/PageConfigProvider/types';
import { usePageManager } from './components/PageManagerProvider/PageManagerProvider';
import { PageNotFound } from './components/PageNotFound';
import { PageStateProvider } from './components/PageState/PageStateProvider';
import { scrollPositionStore } from './components/scroll-position-store';
import { FilterProvider } from './filters/contexts/FilterProvider';
import { FilterId } from './filters/types/filters';
import { useFetchPageConfig } from './hooks/useFetchPageConfig';
import { generatePageDraftConfig } from './utils/generate-page-draft';
import {
  getLocalTemplate,
  getLocalTemplates,
} from './utils/page-config-lookup';
import {
  type EntityType,
  extractEntityTypeFromPath,
  isValidEntityType,
  type RouteAction,
} from './utils/routing-helpers';

const CustomDashContextWrapper = ({
  editMode,
  pageConfig,
  isReport,
}: {
  editMode: boolean;
  pageConfig: Page;
  isReport?: boolean;
}) => {
  const globalAssistantEnabled = useUnleashFlag(FeatureFlag.GlobalAIAssistant);
  const location = useLocation();
  const firstTab = pageConfig.tabs?.[0]?.tabContent;
  const primaryFilter = firstTab?.primaryFilter;

  // Intentionally keyed only on pageConfig.id: reset when navigating to a different page, not on every location.state change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const navState = location.state as { preserveScroll?: boolean } | null;
    if (!navState?.preserveScroll) {
      scrollPositionStore.reset();
    }
  }, [pageConfig.id]);

  usePageVisitTracking({
    page: pageConfig.name,
    additionalProps: {
      page_id: pageConfig.id,
      page_type: pageConfig.type,
    },
  });

  return (
    <DashboardErrorBoundary>
      <EditModeProvider editMode={editMode}>
        <FilterProvider
          pageId={pageConfig.id}
          revelioPageId={pageConfig?.revelioPageId}
          primaryFilterId={primaryFilter?.id}
          primaryFilterSelectionLimit={primaryFilter?.selectionLimit}
          primaryFilterSelectionDefault={primaryFilter?.selectionDefault}
          {...(isReport && {
            defaultSelectionOverrides: {
              [FilterId.COMPANY]: [
                { id: '8_256_1116644', name: 'Workday', levelId: 'company' },
              ],
            },
          })}
        >
          <PageStateProvider
            key={pageConfig.id}
            initialPageState={{
              metricOverrides: {},
              selections: {},
              primaryEntities: {},
              multiPrimaryEntities: {},
              dimensionSourceOverrides: {},
              initialSelections: {},
              activeTabId:
                pageConfig?.tabs?.find((tab) => tab.isActive)?.id ??
                pageConfig?.tabs?.[0]?.id ??
                '',
            }}
          >
            <PageProvider
              key={`${pageConfig.id}-${editMode ? 'edit' : 'view'}`}
              initialConfig={pageConfig}
            >
              {({ tabContent }) => (
                <FullPageError
                  showErrorRatio={DEFAULT_FULL_PAGE_ERROR_THRESHOLD}
                >
                  <CustomDashPreferenceProivder>
                    {globalAssistantEnabled ? (
                      <GlobalAIAssistantBridge />
                    ) : null}
                    <CustomDash key={tabContent.id} tabContent={tabContent} />
                  </CustomDashPreferenceProivder>
                </FullPageError>
              )}
            </PageProvider>
          </PageStateProvider>
          <SyncInitialPreferencesToFilters
            revelioPageId={pageConfig?.revelioPageId}
          />
        </FilterProvider>
      </EditModeProvider>
    </DashboardErrorBoundary>
  );
};

/**
 * CustomDashRoute accepts an optional idOverride that will look up a specific template
 * that's passed in.
 * If no idOverride is provided, it will navigate to the custom-dash creator page
 * If idOverride does not return a template, it will navigate to the home page
 */
export const CustomDashRoute = ({ idOverride }: { idOverride?: string }) => {
  const location = useLocation();
  const { templateId, action } = useParams<{
    templateId: string;
    action: RouteAction;
  }>();

  const entityType = extractEntityTypeFromPath(location.pathname);

  if (idOverride) {
    return <RevelioDashboardPage pageId={idOverride} />;
  }

  if (!isValidEntityType(entityType)) {
    return <Navigate to="/" replace />;
  }

  if (templateId) {
    if (action === 'clone') {
      return (
        <NewCustomPage
          entityType={entityType}
          templateId={templateId}
          action="clone"
        />
      );
    }

    return (
      <UserDashboardPage pageId={templateId} editMode={action === 'edit'} />
    );
  }

  return <NewCustomPage entityType={entityType} />;
};

/** Revelio Dashboard Page
 * Doesn't do any page fetching, but just loads the page from local template
 */
const RevelioDashboardPage = ({ pageId }: { pageId: string }) => {
  const pageConfig = getLocalTemplate(pageId);

  if (!pageConfig) return <PageNotFound />;

  return <CustomDashContextWrapper pageConfig={pageConfig} editMode={false} />;
};

/** User Dashboard Page
 * Fetches the page by id
 */
const UserDashboardPage = ({
  pageId,
  editMode,
}: {
  pageId: string;
  editMode: boolean;
}) => {
  const { data: pageConfig, isLoading } = useFetchPageConfig(pageId);

  useEffect(() => {
    mixpanel.track(BuildADashTrackingEvents.VIEW_CUSTOM_PAGE, {
      page_id: pageId,
    });
  }, [pageId]);

  if (isLoading) return <CenterLoading />;

  if (!pageConfig) return <PageNotFound />;

  return (
    <CustomDashContextWrapper pageConfig={pageConfig} editMode={editMode} />
  );
};
type PageLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: Page }
  | { status: 'error'; error: Error };

function NewCustomPage({
  entityType,
  templateId,
  action,
}: {
  entityType: EntityType;
  templateId?: string;
  action?: 'clone';
}) {
  const { loggedInUser } = useGetLoggedInUser();
  const { pages, loading: pagesLoading } = usePageManager();
  const brand = useBrand();

  const localTemplates = getLocalTemplates();
  const generationKey = `${entityType}:${templateId ?? ''}:${action ?? ''}`;
  const generatedKeyRef = useRef<string>('');

  const [state, setState] = useState<PageLoadState>(() =>
    pagesLoading ? { status: 'loading' } : { status: 'idle' }
  );

  const owner = useMemo(
    () => ({
      id: loggedInUser.id ?? '',
      username: loggedInUser.username ?? '',
    }),
    [loggedInUser.id, loggedInUser.username]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (pagesLoading) {
      setState({ status: 'loading' });
      return;
    }

    // Terminal error: don't retry on incidental dep changes (e.g. `pages`
    // array reference updates). Base's generationKey guard below handles
    // the success terminal case.
    if (state.status === 'error') {
      return;
    }

    // Keep already-generated config unless the route inputs changed.
    if (
      state.status === 'success' &&
      generatedKeyRef.current === generationKey
    ) {
      return;
    }

    setState({ status: 'loading' });
    let cancelled = false;

    (async () => {
      try {
        const config = await generatePageDraftConfig({
          owner,
          existingPages: [...pages, ...localTemplates],
          entityType,
          templateId,
          action,
          clientName: brand.clientName,
        });
        if (cancelled) {
          return;
        }
        generatedKeyRef.current = generationKey;
        setState({ status: 'success', data: config });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState({
          status: 'error',
          error:
            error instanceof Error
              ? error
              : new Error('Failed to generate page configuration'),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    pagesLoading,
    pages,
    owner,
    entityType,
    templateId,
    action,
    brand.clientName,
    generationKey,
    state.status,
  ]);

  if (!loggedInUser?.id) {
    return <Navigate to="/" replace />;
  }

  switch (state.status) {
    case 'idle':
    case 'loading':
      return <CenterLoading />;

    case 'error':
      return <PageNotFound />;

    case 'success':
      return (
        <CustomDashContextWrapper
          pageConfig={state.data}
          editMode
          isReport={entityType === 'report'}
        />
      );
  }
}
